import { useEffect, useState } from 'react';
import { api, ApiError } from '../api';
import type { PublicInvite, User } from '../types';

interface AcceptInviteProps {
  token: string;
  onAccepted: (user: User) => void;
}

export function AcceptInvite({ token, onAccepted }: AcceptInviteProps) {
  const [invite, setInvite] = useState<PublicInvite | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .getInvite(token)
      .then((res) => {
        if (cancelled) return;
        setInvite(res.invite);
        setDisplayName(res.invite.displayName ?? '');
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoadError('This invite link is invalid, expired, or already used.');
        } else {
          setLoadError('Could not load this invite. Try again.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!invite) return;
    if (password.length < 8) {
      setSubmitError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setSubmitError('Passwords don’t match.');
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await api.acceptInvite(token, password, displayName.trim() || null);
      onAccepted(res.user);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        setSubmitError('This invite is no longer valid.');
      } else if (err instanceof ApiError && err.status === 409) {
        setSubmitError('A user with this email already exists. Sign in instead.');
      } else {
        setSubmitError('Could not complete sign-up. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-sm text-center">
          <h1 className="text-2xl font-semibold tracking-tight mb-2">Chat</h1>
          <p className="text-sm text-rose-400 mb-4">{loadError}</p>
          <a
            href="/"
            className="text-xs text-zinc-400 hover:text-zinc-200 transition"
          >
            Back to sign-in
          </a>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <p className="text-sm text-zinc-500">Loading invite…</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold tracking-tight">Chat</h1>
          <p className="mt-1 text-sm text-zinc-400">
            You’ve been invited to <span className="text-zinc-200">{invite.workspaceName}</span>
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Email</label>
            <input
              type="text"
              value={invite.email}
              readOnly
              className="w-full rounded-md border border-zinc-900 bg-zinc-950 px-3 py-2 text-sm text-zinc-500"
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Display name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={invite.email.split('@')[0]}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-[10px] text-zinc-500 mt-1">At least 8 characters.</p>
          </div>

          <div>
            <label className="block text-xs text-zinc-400 mb-1.5">Confirm password</label>
            <input
              type="password"
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          {submitError && <p className="text-xs text-rose-400">{submitError}</p>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-md bg-indigo-500 hover:bg-indigo-400 disabled:opacity-60 px-3 py-2 text-sm font-medium text-white transition"
          >
            {submitting ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="mt-6 text-xs text-center text-zinc-500">
          By creating an account you agree to PGOC’s acceptable-use policy.
          Two-factor authentication can be enabled from Settings after sign-in.
        </p>
      </div>
    </div>
  );
}
